import { useHandler } from "@redotech/react-util/hook";
import {
  CustomerPortalMessage,
  isPortalBuilderMessage,
  MerchantAppMessageSchema,
  PORTAL_BUILDER_MESSAGE_SOURCE,
} from "@redotech/redo-model/portal-builder-message";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearSettingsCache } from "../api";
import { RefreshSettingsContext } from "./settings";

interface PortalBuilderContextValue {
  isPortalBuilderMode: boolean;
  sendMessageToMerchantApp: (message: CustomerPortalMessage) => void;
}

export const PortalBuilderContext = createContext<PortalBuilderContextValue>({
  isPortalBuilderMode: false,
  sendMessageToMerchantApp: () => {},
});

interface PortalBuilderProviderProps {
  children: ReactNode;
}

export const PortalBuilderProvider = memo(function PortalBuilderProvider({
  children,
}: PortalBuilderProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const refreshSettings = useContext(RefreshSettingsContext);

  // Check if we're in portal builder mode via URL param (captured once on mount)
  const [isPortalBuilderMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("portalBuilderMode") === "true";
  });

  // Send message to merchant app (parent window)
  const sendMessageToMerchantApp = useHandler(
    (message: CustomerPortalMessage) => {
      if (!isPortalBuilderMode || !window.parent || window.parent === window) {
        return;
      }

      // Use parentOrigin if we've received a message, otherwise use "*" cautiously
      const targetOrigin = parentOrigin || "*";
      window.parent.postMessage(
        { source: PORTAL_BUILDER_MESSAGE_SOURCE, payload: message },
        targetOrigin,
      );
    },
  );

  // Listen for messages from merchant app
  useEffect(() => {
    if (!isPortalBuilderMode) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // Check if it's a portal builder message
      if (!isPortalBuilderMessage(event.data)) {
        return;
      }

      // Store the origin for secure responses
      setParentOrigin(event.origin);

      // Parse and validate the message
      const parseResult = MerchantAppMessageSchema.safeParse(
        event.data.payload,
      );
      if (!parseResult.success) {
        console.error("Invalid MerchantAppMessage:", parseResult.error);
        sendMessageToMerchantApp({
          type: "error",
          error: "Invalid message format",
        });
        return;
      }

      const message = parseResult.data;

      switch (message.type) {
        case "navigate":
          // Handle navigation request from merchant app
          navigate(message.path);
          break;
        case "aiCommand":
          // Handle AI command from merchant app
          console.log("AI command:", message.command, message.data);
          // TODO: Implement AI command handling
          break;
        case "refresh":
          // Handle refresh request
          window.location.reload();
          break;
        case "settingsUpdate":
          // Handle settings updates from portal builder agent
          if (message.updates.length === 0) {
            // Empty updates means changes were saved - clear cache and fetch fresh from DB
            clearSettingsCache();
            refreshSettings(undefined);
          } else {
            // Preview pending updates
            refreshSettings(message.updates);
          }
          break;
        case "requestDom": {
          // Collect all styles from the page
          const styles: string[] = [];

          // Get all <style> tags
          const styleTags = document.querySelectorAll("style");
          for (const styleTag of styleTags) {
            if (styleTag.textContent) {
              styles.push(styleTag.textContent);
            }
          }

          // Get all linked stylesheets (only same-origin for security)
          const linkTags = document.querySelectorAll('link[rel="stylesheet"]');
          for (const link of linkTags) {
            try {
              const href = (link as HTMLLinkElement).href;
              if (href && new URL(href).origin === window.location.origin) {
                // For same-origin stylesheets, we can access the rules
                const sheet = (link as HTMLLinkElement).sheet;
                if (sheet) {
                  try {
                    const rules = Array.from(sheet.cssRules);
                    const cssText = rules
                      .map((rule) => rule.cssText)
                      .join("\n");
                    if (cssText) {
                      styles.push(cssText);
                    }
                  } catch (e) {
                    // Can't access cross-origin stylesheet rules
                    console.warn("Cannot access stylesheet rules:", href);
                  }
                }
              }
            } catch (e) {
              // Skip invalid URLs or inaccessible stylesheets
              console.warn("Cannot access stylesheet:", e);
            }
          }

          // Send current DOM and styles back to merchant app
          sendMessageToMerchantApp({
            type: "domContext",
            dom: document.body.innerHTML,
            styles: styles.join("\n\n"),
          });
          break;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Send ready message to parent
    sendMessageToMerchantApp({ type: "ready" });

    return () => window.removeEventListener("message", handleMessage);
  }, [
    isPortalBuilderMode,
    navigate,
    refreshSettings,
    sendMessageToMerchantApp,
  ]);

  // Send navigation events to parent
  useEffect(() => {
    if (!isPortalBuilderMode) {
      return;
    }

    sendMessageToMerchantApp({ type: "navigate", path: location.pathname });
  }, [isPortalBuilderMode, location.pathname, sendMessageToMerchantApp]);

  const value = useMemo(
    () => ({ isPortalBuilderMode, sendMessageToMerchantApp }),
    [isPortalBuilderMode, sendMessageToMerchantApp],
  );

  return (
    <PortalBuilderContext.Provider value={value}>
      {children}
    </PortalBuilderContext.Provider>
  );
});
